import React, { useState, useCallback, useRef } from "react";

//----------REDUX IMPORTS --------------------------------
import { useSelector } from "react-redux";

import LiveFeeds from "./LiveFeeds";
import theme from "./UI/Theme";
import PaymentsInMain from "../components/paymentsComponent/PaymentsInMain";
import PaymentsOut from "../components/purchaseComponents/PaymentsOut";
import SalesVsTargetGraph from "./graphComponents/SalesVsTargetGraph";
import MyTabs from "./UI/MyTabs";
import SalesByConsultantSummaryGraph from "./graphComponents/SalesByConsultantSummaryGraph";
import SalesSummary from "./salesComponents/SalesSummary";

//OTHER IMPORTS------------------------------------------
import {
  format,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subYears,
  startOfISOWeek,
  endOfISOWeek,
  subWeeks,
} from "date-fns";

//MUI IMPORTS--------------------------------------------
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Typography from "@mui/material/Typography";

/**
 * LOGIC MOVED OUTSIDE COMPONENT 
 * This ensures the initial date strings are stable and do not trigger re-renders 
 * on the first load of the application.
 */
const getInitialDates = () => {
  let prevMonday = new Date();
  prevMonday.setUTCDate(prevMonday.getUTCDate() - ((prevMonday.getUTCDay() + 6) % 7));
  prevMonday.setHours(0, 0, 0, 0);

  return {
    initial: format(prevMonday, "yyyy-MM-dd"),
    today: format(new Date(), "yyyy-MM-dd"),
  };
};

const STABLE_DATES = getInitialDates();

const MainContent = () => {
  const userInfo = useSelector((state) => state.user);
  const [refresh] = useState(null);

  const matches = useMediaQuery(theme.breakpoints.down("lg"));
  const matches2 = useMediaQuery(theme.breakpoints.down("md"));
  const matches3 = useMediaQuery(theme.breakpoints.down("sm"));

  // State for dates
  const [tabInitialDate, setTabInitialDate] = useState(STABLE_DATES.initial);
  const [tabFinalDate, setTabFinalDate] = useState(STABLE_DATES.today);

  /**
   * Ref for manual comparison. 
   * This acts as a double-lock to prevent re-renders if parent components (Home.js/Main.js)
   * trigger a refresh of MainContent.
   */
  const lastSetDates = useRef({ start: STABLE_DATES.initial, end: STABLE_DATES.today });

  const updateDatesIfChanged = useCallback((start, end) => {
    if (start !== lastSetDates.current.start || end !== lastSetDates.current.end) {
      lastSetDates.current = { start, end };
      setTabInitialDate(start);
      setTabFinalDate(end);
    }
  }, []);

  // Memoized date selector
  const dateSelectorHandler = useCallback((dateFrom, dateTo) => {
    const start = format(new Date(dateFrom), "yyyy-MM-dd");
    const end = format(new Date(dateTo), "yyyy-MM-dd");
    updateDatesIfChanged(start, end);
  }, [updateDatesIfChanged]);

  // Memoized tab handler
  const changeTabsHandler = useCallback((index) => {
    const now = new Date();
    let start;
    let end;

    if (index === 0) {
      start = STABLE_DATES.initial;
      end = STABLE_DATES.today;
    } else if (index === 1) {
      const prevWeek = subWeeks(now, 1);
      start = format(startOfISOWeek(prevWeek), "yyyy-MM-dd");
      end = format(endOfISOWeek(prevWeek), "yyyy-MM-dd");
    } else if (index === 2) {
      start = format(startOfMonth(now), "yyyy-MM-dd");
      end = format(now, "yyyy-MM-dd");
    } else if (index === 3) {
      const lastMonth = subMonths(now, 1);
      start = format(startOfMonth(lastMonth), "yyyy-MM-dd");
      end = format(endOfMonth(lastMonth), "yyyy-MM-dd");
    } else if (index === 4) {
      start = format(startOfYear(now), "yyyy-MM-dd");
      end = format(now, "yyyy-MM-dd");
    } else if (index === 5) {
      const lastYear = subYears(now, 1);
      start = format(startOfYear(lastYear), "yyyy-MM-dd");
      end = format(endOfYear(lastYear), "yyyy-MM-dd");
    }

    if (start && end) {
      updateDatesIfChanged(start, end);
    }
  }, [updateDatesIfChanged]);

  return (
    <Box
      component="main"
      sx={{
        backgroundImage:
          "url(https://ik.imagekit.io/bwcdq46tkc8/LudisGroup/Ludisbackground-2021_F9WodH70V.jpg?ik-sdk-version=javascript-1.4.3&updatedAt=1648720587132)",
        flexGrow: 1,
        height: "100vh",
        overflow: "auto",
        marginTop: 0,
      }}
    >
      {!matches3 && <Toolbar />}

      <Container maxWidth="lg" sx={{ mt: matches3 ? 0 : 4, mb: 3 }}>
        <MyTabs
          changeTabsHandler={changeTabsHandler}
          dateSelectorHandler={dateSelectorHandler}
          tabContent="MainDashboardTaps"
        >
          <Typography
            align="center"
            variant="body2"
            color="text.secondary"
            sx={{ margin: "auto", mb: 3 }}
          >
            {!matches3 && "Information "}
            from: -{" "}
            {new Date(tabInitialDate).toLocaleString("default", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}{" "}
            - to -{" "}
            {new Date(tabFinalDate).toLocaleString("default", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </Typography>

          <Grid container spacing={3}>
            {/* Sales Summary */}
            <Grid item xs={12} md={6} lg={3}>
              <Paper
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  height: matches2 ? "auto" : matches ? 370 : 335,
                  paddingBottom: 0,
                  minWidth: 230,
                }}
              >
                <SalesSummary
                  tabInitialDate={tabInitialDate}
                  tabFinalDate={tabFinalDate}
                />
              </Paper>
            </Grid>

            {/* Payments In */}
            {userInfo.permission >= 5 && (
              <Grid item xs={12} md={6} lg={3}>
                <Paper
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    height: matches2 ? "auto" : matches ? 370 : 335,
                    paddingBottom: 0,
                    minWidth: 230,
                  }}
                >
                  <PaymentsInMain
                    tabInitialDate={tabInitialDate}
                    tabFinalDate={tabFinalDate}
                    refresh={refresh}
                    MainContentReport={true}
                  />
                </Paper>
              </Grid>
            )}

            {/* Payments Out */}
            {userInfo.permission >= 5 && (
              <Grid item xs={12} md={6} lg={3}>
                <Paper
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    height: 335,
                    paddingBottom: 0,
                    minWidth: 230,
                  }}
                >
                  <PaymentsOut
                    tabInitialDate={tabInitialDate}
                    tabFinalDate={tabFinalDate}
                    refresh={refresh}
                  />
                </Paper>
              </Grid>
            )}

            {/* Branding / Placeholder */}
            {!matches2 && (
              <Grid item xs={12} md={6} lg={3}>
                <Paper
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: 335,
                    paddingBottom: 0,
                    minWidth: 230,
                    backgroundImage:
                      "url(https://ik.imagekit.io/bwcdq46tkc8/LudisGroup/Ludis-Logo-2020_85kTiekBI.png?ik-sdk-version=javascript-1.4.3&updatedAt=1650977432909)",
                    backgroundSize: "contain",
                    backgroundRepeat: "no-repeat",
                  }}
                />
              </Grid>
            )}

            {/* Graphs Section */}
            <Grid item xs={12} md={12} lg={12}>
              <Paper
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  height: "auto",
                  paddingBottom: 0,
                  minWidth: 230,
                }}
              >
                <SalesByConsultantSummaryGraph
                  tabInitialDate={tabInitialDate}
                  tabFinalDate={tabFinalDate}
                />
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
                <SalesVsTargetGraph
                  tabInitialDate={tabInitialDate}
                  tabFinalDate={tabFinalDate}
                />
              </Paper>
            </Grid>

            {/* Live Feeds */}
            <Grid item xs={12} sx={{ marginBottom: 5 }}>
              <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
                <LiveFeeds
                  selectedDatesFrom={tabInitialDate}
                  selectedDatesTo={tabFinalDate}
                />
              </Paper>
            </Grid>
          </Grid>
        </MyTabs>
      </Container>
    </Box>
  );
};

export default React.memo(MainContent);